import {
  fetchRecentDailyAggregates,
  fetchDailyAggregatesForDateRange,
} from "../../repositories/operationsRepository";
import { DailyAggregateRow } from "../../types/operations";
import { DayData, TableRow } from "../../types";
import { HistoryMachineType } from "../../constants/historyMachineTypeFilters";

// =============================================
// Number of days to include in the default
// dashboard data window.
// =============================================
const DEFAULT_RECENT_DAYS = 7;

// =============================================
// Transform the flat list of DailyAggregateRow
// records returned by the view into the DayData
// shape expected by DataTable and PieChartSection.
//
// Each DayData groups all machine/shift rows
// that share the same date, and converts the
// total_minutes integer from the view back into
// totalSeconds for display consistency.
// =============================================
function transformAggregatesToDayData(
  aggregateRows: DailyAggregateRow[]
): DayData[] {
  // Group rows by date string.
  const rowsByDate = new Map<string, TableRow[]>();

  for (const aggregate of aggregateRows) {
    const dateKey = String(aggregate.date);

    if (!rowsByDate.has(dateKey)) {
      rowsByDate.set(dateKey, []);
    }

    const tableRow: TableRow = {
      machineType: aggregate.machine_type,
      machineNumber: aggregate.machine_number,
      shift: aggregate.shift,
      totalParts: Number(aggregate.total_parts),
      // Convert minutes back to seconds for component compatibility.
      // The view stores SUM(duration_seconds) / 60 as integer minutes.
      totalSeconds: Number(aggregate.total_minutes) * 60,
    };

    rowsByDate.get(dateKey)!.push(tableRow);
  }

  const dayDataList: DayData[] = [];

  rowsByDate.forEach((tableRows, date) => {
    dayDataList.push({ date, data: tableRows });
  });

  // Sort chronologically so consumers receive pre-sorted data.
  dayDataList.sort((a, b) => a.date.localeCompare(b.date));

  return dayDataList;
}

// =============================================
// Fetch the most recent N days of aggregated
// operation data and return it as DayData[]
// for direct consumption by dashboard components.
// =============================================
export async function fetchRecentDayData(
  numberOfDays: number = DEFAULT_RECENT_DAYS
): Promise<DayData[]> {
  const aggregateRows = await fetchRecentDailyAggregates(numberOfDays);
  return transformAggregatesToDayData(aggregateRows);
}

// =============================================
// Fetch aggregated operation data for a specific
// inclusive date range, filtered to one machine
// type, and return it as DayData[].
//
// startDate and endDate must be ISO 8601 date
// strings (e.g. "2026-04-28"). machineType must
// be one of "CNC", "Edge", or "Saw".
//
// The transformer is shared with fetchRecentDayData
// so the shape returned is identical and all
// existing visualization components consume it
// directly without changes.
// =============================================
export async function fetchDayDataForDateRange(
  startDate: string,
  endDate: string,
  machineType: HistoryMachineType
): Promise<DayData[]> {
  const aggregateRows = await fetchDailyAggregatesForDateRange(
    startDate,
    endDate,
    machineType
  );
  return transformAggregatesToDayData(aggregateRows);
}
